import { io, operation, simplify } from 'jsts';

// Display only. Distances are metres in a local equirectangular projection,
// not degrees. See docs/territory-display-areas.md for limits and rationale.
export const AREA_TOLERANCE = Object.freeze({ joinRadius: 125, simplify: 35 });
const RADIANS = Math.PI / 180;
const METRES_PER_DEGREE = 6371008.8 * RADIANS;
const unavailable = () => ({ geometry: null, anchor: null, status: 'unavailable' });
const reader = new io.GeoJSONReader();
const writer = new io.GeoJSONWriter();
const union = (shape) => operation.union.UnaryUnionOp.union(shape);
const usable = (shape) => shape && !shape.isEmpty() && shape.getArea() > 0 &&
  shape.isValid() && ['Polygon', 'MultiPolygon'].includes(shape.getGeometryType());

export function generalizeTerritory(geometry, tolerance = AREA_TOLERANCE) {
  if (!geometry) return unavailable();
  try {
    if (geometry.type !== 'MultiPolygon' || !geometry.coordinates.length ||
        geometry.coordinates.length > 3000 || tolerance.joinRadius < 0 ||
        tolerance.simplify < 0 || !Number.isFinite(tolerance.joinRadius) ||
        !Number.isFinite(tolerance.simplify)) return unavailable();
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    let count = 0;
    geometry.coordinates.forEach((polygon) => polygon.forEach((ring) => {
      if (ring.length < 4 || ring[0][0] !== ring[ring.length - 1][0] ||
          ring[0][1] !== ring[ring.length - 1][1]) throw new Error('Open ring');
      ring.forEach(([x, y]) => {
        if (!Number.isFinite(x) || !Number.isFinite(y) || Math.abs(x) > 180 || Math.abs(y) > 85)
          throw new Error('Unsupported coordinate');
        count += 1;
        minX = Math.min(minX, x); maxX = Math.max(maxX, x);
        minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      });
    }));
    if (!count || count > 30000) return unavailable();
    const origin = [(minX + maxX) / 2, (minY + maxY) / 2];
    const xScale = METRES_PER_DEGREE * Math.cos(origin[1] * RADIANS);
    // Fail explicitly outside local-territory scope (including dateline wraps).
    if (maxX - minX > 10 || (maxX - minX) * xScale > 200000 ||
        (maxY - minY) * METRES_PER_DEGREE > 200000) return unavailable();
    const project = ([x, y]) => [(x - origin[0]) * xScale, (y - origin[1]) * METRES_PER_DEGREE];
    const unproject = ([x, y]) => [x / xScale + origin[0], y / METRES_PER_DEGREE + origin[1]];
    const coordinates = geometry.coordinates.map((polygon) => polygon.map((ring) => ring.map(project)));
    // Validate polygons individually: overlaps/duplicates between source zones
    // are allowed, but invalid shells/holes are not silently reinterpreted.
    if (!coordinates.every((polygon) => usable(reader.read({ type: 'Polygon', coordinates: polygon }))))
      return unavailable();
    const dissolved = union(reader.read({ type: 'MultiPolygon', coordinates }));
    if (!usable(dissolved)) return unavailable();
    let result = dissolved;
    let status = 'dissolved';
    try {
      // Morphological closing joins only nearby pieces, never a global hull.
      const closed = tolerance.joinRadius === 0 ? dissolved :
        dissolved.buffer(tolerance.joinRadius, 4).buffer(-tolerance.joinRadius, 4);
      // Restore tiny pieces or thin peninsulas lost to buffer precision.
      const restored = union(dissolved.getFactory().createGeometryCollection([closed, dissolved]));
      const candidate = simplify.TopologyPreservingSimplifier.simplify(restored, tolerance.simplify);
      if (usable(candidate)) { result = candidate; status = 'generalized'; }
    } catch (error) {
      // A valid dissolved fallback still removes internal zone strokes.
    }
    let largest = result.getGeometryN(0);
    for (let i = 1; i < result.getNumGeometries(); i += 1) {
      if (result.getGeometryN(i).getArea() > largest.getArea()) largest = result.getGeometryN(i);
    }
    const point = largest.getInteriorPoint().getCoordinate();
    const output = writer.write(result);
    const polygons = output.type === 'Polygon' ? [output.coordinates] : output.coordinates;
    return {
      geometry: { type: 'MultiPolygon', coordinates: polygons.map((p) => p.map((r) => r.map(unproject))) },
      anchor: unproject([point.x, point.y]), status,
    };
  } catch (error) {
    return unavailable();
  }
}

// Per-controller/business, memory only. Content keys reuse geometry across
// metrics-only API snapshots. Entry AND byte limits prevent unbounded retention.
export function createDisplayAreaCache(transform = generalizeTerritory) {
  const cache = new Map();
  let bytes = 0;
  return (geometry, tolerance = AREA_TOLERANCE) => {
    if (!geometry) return unavailable();
    const key = JSON.stringify([tolerance.joinRadius, tolerance.simplify, geometry]);
    if (cache.has(key)) return cache.get(key);
    const result = transform(geometry, tolerance);
    const size = 2 * (key.length + JSON.stringify(result).length);
    if (size <= 2000000) {
      while (cache.size >= 64 || bytes + size > 2000000) {
        const first = cache.keys().next().value;
        bytes -= cache.get(first).size;
        cache.delete(first);
      }
      // Non-enumerable accounting is never part of the display/API payload.
      Object.defineProperty(result, 'size', { value: size });
      cache.set(key, result);
      bytes += size;
    }
    return result;
  };
}
